import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';

interface SystemLog {
  id: string;
  service: string;
  logGroup: string;
  level: string;
  timestamp: string;
  message: string;
}

interface LogsResponse {
  items: SystemLog[];
  total: number;
  limit: number;
  hours: number;
}

const services = [
  'Auth',
  'Events',
  'Gallery',
  'Orders',
  'Admin',
  'StripeWebhook',
  'ImageProcessor',
  'PhotographerApi',
];

const levelClasses: Record<string, string> = {
  ERROR: 'bg-red-50 text-red-700',
  WARNING: 'bg-yellow-50 text-yellow-700',
  INFO: 'bg-blue-50 text-blue-700',
  DEBUG: 'bg-gray-100 text-gray-700',
};

const SystemLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [service, setService] = useState('');
  const [level, setLevel] = useState('');
  const [hours, setHours] = useState(24);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get<{ data: LogsResponse }>('/admin/system-logs', {
        params: {
          limit: 50,
          hours,
          ...(service ? { service } : {}),
          ...(level ? { level } : {}),
        },
      });
      setLogs(response.data.data.items);
    } catch {
      setError('Failed to load system logs.');
    } finally {
      setLoading(false);
    }
  }, [hours, level, service]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">System Logs</h1>
            <p className="mt-1 text-gray-500">Recent service logs pulled from the API</p>
          </div>
          <button
            onClick={loadLogs}
            className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200"
          >
            Refresh
          </button>
        </div>

        <div className="mb-6 rounded-lg bg-white p-4 shadow">
          <div className="flex flex-wrap gap-4">
            <select
              value={service}
              onChange={event => setService(event.target.value)}
              className="rounded-lg border border-gray-300 px-4 py-2"
            >
              <option value="">All Services</option>
              {services.map(name => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <select
              value={level}
              onChange={event => setLevel(event.target.value)}
              className="rounded-lg border border-gray-300 px-4 py-2"
            >
              <option value="">All Levels</option>
              <option value="ERROR">Error</option>
              <option value="WARNING">Warning</option>
              <option value="INFO">Info</option>
              <option value="DEBUG">Debug</option>
            </select>
            <select
              value={hours}
              onChange={event => setHours(Number(event.target.value))}
              className="rounded-lg border border-gray-300 px-4 py-2"
            >
              <option value={1}>Last hour</option>
              <option value={24}>Last 24 hours</option>
              <option value={72}>Last 3 days</option>
              <option value={168}>Last 7 days</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        )}

        <div className="overflow-hidden rounded-lg bg-white shadow">
          {loading ? (
            <div className="p-12 text-center text-gray-500">Loading system logs...</div>
          ) : logs.length === 0 ? (
            <div className="p-12 text-center text-gray-500">No log events found</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {logs.map(log => (
                <div key={log.id} className="px-6 py-4">
                  <div className="mb-2 flex flex-wrap items-center gap-3">
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-semibold ${
                        levelClasses[log.level] || levelClasses.INFO
                      }`}
                    >
                      {log.level}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{log.service}</span>
                    <span className="text-sm text-gray-500">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <pre className="whitespace-pre-wrap break-words text-sm text-gray-700">
                    {log.message}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemLogsPage;
